import Icon from '@/components/ui/AppIcon';

interface Document {
  id: string;
  title: string;
  description: string;
  category: string;
  fileSize: string;
  lastUpdated: string;
  accessLevel: 'public' | 'restricted' | 'confidential';
  icon: string;
}

interface DocumentLibraryProps {
  selectedPortal: string | null;
  onDocumentRequest: (documentId: string) => void;
  className?: string;
}

const DocumentLibrary = ({ selectedPortal, onDocumentRequest, className = '' }: DocumentLibraryProps) => {
  const documents: Document[] = [
    {
      id: 'investment-deck-2026',
      title: 'Investment Deck 2026',
      description: 'Comprehensive overview of investment opportunities, financial projections, and risk mitigation strategies.',
      category: 'Investment Materials',
      fileSize: '8.5 MB',
      lastUpdated: '15 Jan 2026',
      accessLevel: 'confidential',
      icon: 'PresentationChartLineIcon'
    },
    {
      id: 'due-diligence-package',
      title: 'Due Diligence Package',
      description: 'Complete documentation for investment committee review including financial statements, legal structure, and compliance certifications.',
      category: 'Investment Materials',
      fileSize: '24.3 MB',
      lastUpdated: '10 Jan 2026',
      accessLevel: 'confidential',
      icon: 'DocumentMagnifyingGlassIcon'
    },
    {
      id: 'q4-2025-impact-report',
      title: 'Q4 2025 Impact Report',
      description: 'Quarterly performance metrics, impact measurements, and progress against transformation agenda milestones.',
      category: 'Performance Reports',
      fileSize: '5.2 MB',
      lastUpdated: '08 Jan 2026',
      accessLevel: 'restricted',
      icon: 'ChartBarIcon'
    },
    {
      id: 'partnership-framework',
      title: 'Partnership Framework Guide',
      description: 'Detailed overview of collaboration structures, engagement protocols, and partnership development process.',
      category: 'Partnership Documentation',
      fileSize: '3.8 MB',
      lastUpdated: '20 Dec 2025',
      accessLevel: 'restricted',
      icon: 'DocumentTextIcon'
    },
    {
      id: 'kenya-regulatory-compliance',
      title: 'Kenya Regulatory Compliance Report',
      description: 'Comprehensive compliance documentation for Kenyan government partnerships and regulatory requirements.',
      category: 'Government Relations',
      fileSize: '6.7 MB',
      lastUpdated: '18 Dec 2025',
      accessLevel: 'restricted',
      icon: 'ShieldCheckIcon'
    },
    {
      id: 'sector-opportunities-2026',
      title: 'Sector Opportunities Analysis 2026',
      description: 'In-depth analysis of investment opportunities across agriculture, manufacturing, infrastructure, and technology sectors.',
      category: 'Research & Analysis',
      fileSize: '12.1 MB',
      lastUpdated: '15 Dec 2025',
      accessLevel: 'restricted',
      icon: 'ChartPieIcon'
    },
    {
      id: 'transformation-agenda-whitepaper',
      title: 'Transformation Agenda White Paper',
      description: 'Foundational document outlining the 20-year vision, three-phase implementation strategy, and expected outcomes.',
      category: 'Research & Analysis',
      fileSize: '4.5 MB',
      lastUpdated: '01 Dec 2025',
      accessLevel: 'public',
      icon: 'DocumentChartBarIcon'
    },
    {
      id: 'impact-measurement-framework',
      title: 'Impact Measurement Framework',
      description: 'Methodology for tracking and reporting social, economic, and environmental impact across all investments.',
      category: 'Research & Analysis',
      fileSize: '2.9 MB',
      lastUpdated: '25 Nov 2025',
      accessLevel: 'public',
      icon: 'ScaleIcon'
    }
  ];

  const getAccessLevelBadge = (level: string) => {
    const badges = {
      public: { bg: 'bg-success/10', text: 'text-success', label: 'Public Access' },
      restricted: { bg: 'bg-warning/10', text: 'text-warning', label: 'Restricted Access' },
      confidential: { bg: 'bg-error/10', text: 'text-error', label: 'Confidential' }
    };
    return badges[level as keyof typeof badges];
  };

  return (
    <section className={`py-16 bg-card ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-headline text-3xl md:text-4xl font-semibold text-foreground mb-4">
            Document Library
          </h2>
          <p className="font-body text-lg text-text-secondary max-w-3xl mx-auto">
            Secure access to comprehensive documentation, research reports, and partnership materials.
          </p>
        </div>

        {!selectedPortal && (
          <div className="bg-muted border border-border rounded-lg p-8 text-center max-w-2xl mx-auto mb-8">
            <Icon name="LockClosedIcon" size={48} variant="outline" className="text-muted-foreground mx-auto mb-4" />
            <h3 className="font-headline text-xl font-semibold text-foreground mb-2">
              Select a Portal to Access Documents
            </h3>
            <p className="font-body text-sm text-text-secondary">
              Choose your stakeholder portal above to view and request access to relevant documentation.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.map((doc) => {
            const badge = getAccessLevelBadge(doc.accessLevel);
            const isAccessible = selectedPortal !== null;

            return (
              <div
                key={doc.id}
                className={`bg-background border border-border rounded-lg p-6 hover:shadow-md transition-all duration-300 ${!isAccessible ? 'opacity-50' : ''}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                    <Icon name={doc.icon as any} size={24} variant="outline" className="text-primary" />
                  </div>
                  <span className={`px-2 py-1 ${badge.bg} ${badge.text} text-xs font-body font-medium rounded`}>
                    {badge.label}
                  </span>
                </div>

                <h3 className="font-headline text-lg font-semibold text-foreground mb-2">
                  {doc.title}
                </h3>

                <p className="font-body text-sm text-text-secondary leading-relaxed mb-4">
                  {doc.description}
                </p>

                <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                  <span className="flex items-center space-x-1">
                    <Icon name="FolderIcon" size={14} variant="outline" />
                    <span>{doc.category}</span>
                  </span>
                  <span>{doc.fileSize}</span>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <span className="font-body text-xs text-muted-foreground">
                    Updated {doc.lastUpdated}
                  </span>
                  <button
                    onClick={() => onDocumentRequest(doc.id)}
                    disabled={!isAccessible}
                    className={`px-4 py-2 font-body text-sm font-medium rounded-md transition-all duration-300 flex items-center space-x-1 ${
                      isAccessible
                        ? 'bg-secondary text-secondary-foreground hover:bg-secondary/90'
                        : 'bg-muted text-muted-foreground cursor-not-allowed'
                    }`}
                  >
                    <Icon name="ArrowDownTrayIcon" size={14} variant="outline" />
                    <span>Request</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default DocumentLibrary;